import React, { lazy, Suspense } from 'react';
import { CodeSplitWrapper } from '@/components/ui/code-split-wrapper';
import { PageTransition } from '@/components/PageTransition';

// Lazy load route components for better performance
export const LazyIndex = lazy(() => import('@/pages/Index'));
export const LazyReports = lazy(() => import('@/pages/Reports'));
export const LazyDevices = lazy(() => import('@/pages/Devices'));
export const LazyLaunchpad = lazy(() => import('@/pages/Launchpad'));
export const LazyOnboarding = lazy(() => import('@/pages/Onboarding'));
export const LazyAbout = lazy(() => import('@/pages/About'));
export const LazySupport = lazy(() => import('@/pages/Support'));
export const LazyCheckout = lazy(() => import('@/pages/Checkout'));
export const LazyQuotationSuccess = lazy(() => import('@/pages/QuotationSuccess'));
export const LazyOrders = lazy(() => import('@/pages/Orders'));
export const LazyAccount = lazy(() => import('@/pages/Account'));
export const LazyPaymentSuccess = lazy(() => import('@/pages/PaymentSuccess'));
export const LazyPaymentCanceled = lazy(() => import('@/pages/PaymentCanceled'));
export const LazyAdminLogin = lazy(() => import('@/pages/AdminLogin'));
export const LazyAdminDashboard = lazy(() => import('@/pages/AdminDashboard'));
export const LazyPrivacyPolicy = lazy(() => import('@/pages/PrivacyPolicy'));
export const LazyTermsOfService = lazy(() => import('@/pages/TermsOfService'));
export const LazyCookiePolicy = lazy(() => import('@/pages/CookiePolicy'));
export const LazyNotFound = lazy(() => import('@/pages/NotFound'));

// Route wrapper component that includes code splitting + page transition
interface RouteWrapperProps {
  children: React.ReactNode;
  componentName: string;
}

export const RouteWrapper: React.FC<RouteWrapperProps> = ({ children, componentName }) => (
  <CodeSplitWrapper 
    name={componentName}
    fallback={
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading {componentName}...</p>
        </div>
      </div>
    }
  >
    <PageTransition>
      {children}
    </PageTransition>
  </CodeSplitWrapper>
);

// Enhanced route components with code splitting wrappers
export const IndexRoute = () => (
  <RouteWrapper componentName="Home">
    <LazyIndex />
  </RouteWrapper>
);

export const ReportsRoute = () => (
  <RouteWrapper componentName="Reports">
    <LazyReports />
  </RouteWrapper>
);

export const DevicesRoute = () => (
  <RouteWrapper componentName="Devices">
    <LazyDevices />
  </RouteWrapper>
);

export const LaunchpadRoute = () => (
  <RouteWrapper componentName="Launchpad">
    <LazyLaunchpad />
  </RouteWrapper>
);

export const OnboardingRoute = () => (
  <RouteWrapper componentName="Onboarding">
    <LazyOnboarding />
  </RouteWrapper>
);

export const AboutRoute = () => (
  <RouteWrapper componentName="About">
    <LazyAbout />
  </RouteWrapper>
);

export const SupportRoute = () => (
  <RouteWrapper componentName="Support">
    <LazySupport />
  </RouteWrapper>
);

export const CheckoutRoute = () => (
  <RouteWrapper componentName="Checkout">
    <LazyCheckout />
  </RouteWrapper>
);

export const QuotationSuccessRoute = () => (
  <RouteWrapper componentName="Quotation Success">
    <LazyQuotationSuccess />
  </RouteWrapper>
);

export const OrdersRoute = () => (
  <RouteWrapper componentName="Orders">
    <LazyOrders />
  </RouteWrapper>
);

export const AccountRoute = () => (
  <RouteWrapper componentName="Account">
    <LazyAccount />
  </RouteWrapper>
);

export const PaymentSuccessRoute = () => (
  <RouteWrapper componentName="Payment Success">
    <LazyPaymentSuccess />
  </RouteWrapper>
);

export const PaymentCanceledRoute = () => (
  <RouteWrapper componentName="Payment Canceled">
    <LazyPaymentCanceled />
  </RouteWrapper>
);

export const AdminLoginRoute = () => (
  <RouteWrapper componentName="Admin Login">
    <LazyAdminLogin />
  </RouteWrapper>
);

export const AdminDashboardRoute = () => (
  <RouteWrapper componentName="Admin Dashboard">
    <LazyAdminDashboard />
  </RouteWrapper>
);

export const PrivacyPolicyRoute = () => (
  <RouteWrapper componentName="Privacy Policy">
    <LazyPrivacyPolicy />
  </RouteWrapper>
);

export const TermsOfServiceRoute = () => (
  <RouteWrapper componentName="Terms of Service">
    <LazyTermsOfService />
  </RouteWrapper>
);

export const CookiePolicyRoute = () => (
  <RouteWrapper componentName="Cookie Policy">
    <LazyCookiePolicy />
  </RouteWrapper>
);

export const NotFoundRoute = () => (
  <RouteWrapper componentName="Not Found">
    <LazyNotFound />
  </RouteWrapper>
);